import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  DEFAULT_PRIMARY_COLOR,
  DEFAULT_SECONDARY_COLOR,
  DEFAULT_THEME_PRESET,
  getThemeCssVariables,
  normalizeThemeColors,
} from "../utils/themePalette";

/* ── Preset themes ────────────────────────────────────────────────── */
export const THEME_PRESETS = [
  {
    id:        DEFAULT_THEME_PRESET,
    name:      "Navy & Gold",
    primary:   DEFAULT_PRIMARY_COLOR,
    secondary: DEFAULT_SECONDARY_COLOR,
  },
  {
    id:        "forest",
    name:      "Forest",
    primary:   "#064E3B",
    secondary: "#34D399",
  },
  {
    id:        "ocean",
    name:      "Ocean",
    primary:   "#0C4A6E",
    secondary: "#38BDF8",
  },
  {
    id:        "plum",
    name:      "Plum",
    primary:   "#4C1D95",
    secondary: "#C4B5FD",
  },
  {
    id:        "crimson",
    name:      "Crimson",
    primary:   "#7F1D1D",
    secondary: "#FCA5A5",
  },
  {
    id:        "graphite",
    name:      "Graphite",
    primary:   "#1E293B",
    secondary: "#94A3B8",
  },
];

/* ── Utilities ────────────────────────────────────────────────────── */

/** Push both colour values into CSS custom properties on <html>. */
function applyColorsToDOM(primary, secondary, preset) {
  const root = document.documentElement;
  const normalizedTheme = normalizeThemeColors({ primary, secondary, preset });
  const cssVariables = getThemeCssVariables(normalizedTheme);

  Object.entries(cssVariables).forEach(([token, value]) => {
    root.style.setProperty(token, value);
  });

  return normalizedTheme;
}

/* ── Store ────────────────────────────────────────────────────────── */
const useThemeStore = create(
  persist(
    (set, get) => ({
      /* ── State ──────────────────────────────────────────────────── */
      primaryColor:   DEFAULT_PRIMARY_COLOR,
      secondaryColor: DEFAULT_SECONDARY_COLOR,
      preset:         DEFAULT_THEME_PRESET,

      /* ── Actions ────────────────────────────────────────────────── */

      /** Apply a named preset. */
      setPreset(presetId) {
        const found = THEME_PRESETS.find((p) => p.id === presetId);
        if (!found) return;
        const normalizedTheme = applyColorsToDOM(found.primary, found.secondary, presetId);
        set({
          primaryColor:   normalizedTheme.primary,
          secondaryColor: normalizedTheme.secondary,
          preset:         normalizedTheme.preset,
        });
      },

      /** Change only the primary colour (marks preset as "custom"). */
      setPrimaryColor(color) {
        const normalizedTheme = applyColorsToDOM(color, get().secondaryColor, "custom");
        set({
          primaryColor: normalizedTheme.primary,
          secondaryColor: normalizedTheme.secondary,
          preset: normalizedTheme.preset,
        });
      },

      /** Change only the secondary/accent colour (marks preset as "custom"). */
      setSecondaryColor(color) {
        const normalizedTheme = applyColorsToDOM(get().primaryColor, color, "custom");
        set({
          primaryColor: normalizedTheme.primary,
          secondaryColor: normalizedTheme.secondary,
          preset: normalizedTheme.preset,
        });
      },

      /** Load a saved theme object from Convex (called after auth). */
      loadSavedTheme({ primary, secondary, preset } = {}) {
        if (!primary || !secondary) return;
        const normalizedTheme = applyColorsToDOM(primary, secondary, preset ?? "custom");
        set({
          primaryColor:   normalizedTheme.primary,
          secondaryColor: normalizedTheme.secondary,
          preset:         normalizedTheme.preset,
        });
      },

      /** Re-apply the currently stored colours to the DOM (called on app boot). */
      applyCurrentTheme() {
        const { primaryColor, secondaryColor, preset } = get();
        const normalizedTheme = applyColorsToDOM(primaryColor, secondaryColor, preset);

        if (
          normalizedTheme.primary !== primaryColor ||
          normalizedTheme.secondary !== secondaryColor ||
          normalizedTheme.preset !== preset
        ) {
          set({
            primaryColor: normalizedTheme.primary,
            secondaryColor: normalizedTheme.secondary,
            preset: normalizedTheme.preset,
          });
        }
      },

      /** Reset to the default Navy & Gold preset. */
      resetToDefault() {
        get().setPreset(DEFAULT_THEME_PRESET);
      },
    }),
    {
      name:        "wise-theme",
      partialize:  (s) => ({
        primaryColor:   s.primaryColor,
        secondaryColor: s.secondaryColor,
        preset:         s.preset,
      }),
    }
  )
);

export default useThemeStore;
